import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VList, type VListHandle } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui/IconButton';
import { useInViewport } from '@/hooks/useInViewport';
import { logError } from '@/lib/errorLogging';
import { settingsStore, TypeScale } from '@/stores/settings.store';
import { uiStore } from '@/stores/ui.store';
import { classList } from '@/utils/cn';

import { StaticExportContext } from '../../static-export-context';

import {
  chunkCodeLinesForHighlighting,
  isExpectedSyntaxHighlightError,
  peekCachedCodeHighlight,
  requestCodeHighlight,
  shouldEagerHighlightCode,
  splitCodeLinesForHighlighting,
  VIRTUALIZED_CODE_CHUNK_LINE_COUNT,
} from './syntax-highlight.service';

import type { StaticCodeProps } from './StaticCode';
import type {
  SyntaxHighlightLine,
  SyntaxHighlightTheme,
} from './syntax-highlight.types';
import type { BundledLanguage } from 'shiki';

// Only full-block highlight small snippets. Larger blocks use virtualized
// chunk highlighting so Shiki never parses thousands of offscreen lines.
const VIRTUALIZED_CODE_BLOCK_MAX_HEIGHT_PX = 550;
const VIRTUALIZED_CODE_BLOCK_OVERSCAN = 1;
const VIRTUALIZED_CODE_SCROLL_THRESHOLD_PX = 1;
const VIRTUALIZED_CODE_BOTTOM_FOLLOW_THRESHOLD_PX = 50;
const RENDERED_HIGHLIGHT_CACHE_LIMIT = 100;
const renderedHighlightCache = new Map<string, React.ReactElement[]>();

function getCachedRenderedHighlight(cacheKey: string) {
  const cachedElements = renderedHighlightCache.get(cacheKey);
  if (!cachedElements) return null;

  renderedHighlightCache.delete(cacheKey);
  renderedHighlightCache.set(cacheKey, cachedElements);
  return cachedElements;
}

function setCachedRenderedHighlight(
  cacheKey: string,
  elements: React.ReactElement[]
) {
  renderedHighlightCache.set(cacheKey, elements);

  if (renderedHighlightCache.size > RENDERED_HIGHLIGHT_CACHE_LIMIT) {
    const oldestKey = renderedHighlightCache.keys().next().value;
    if (oldestKey !== undefined) renderedHighlightCache.delete(oldestKey);
  }
}

const renderPlainCodeChunk = (chunkText: string, lineClassName: string) =>
  renderPlainLines(splitCodeLinesForHighlighting(chunkText), lineClassName);

const renderPlainLines = (
  lines: string[],
  lineClassName: string,
  keyOffset = 0
) =>
  lines.map((line, index) => (
    <div key={keyOffset + index} className={lineClassName}>
      {line || '\u00A0'}
    </div>
  ));

const renderHighlightedLines = (
  lines: SyntaxHighlightLine[],
  {
    keyOffset = 0,
    lineClassName,
    lineWrap,
  }: {
    keyOffset?: number;
    lineClassName?: string;
    lineWrap?: boolean;
  } = {}
) =>
  lines.map((line, lineIndex) => (
    <div
      key={keyOffset + lineIndex}
      className={lineClassName}
      style={lineClassName ? undefined : { display: 'block' }}
    >
      {line.length > 0
        ? line.map((token, tokenIndex) => (
            <span
              key={tokenIndex}
              style={{
                color: token.color,
                whiteSpace: lineWrap ? 'pre-wrap' : 'unset',
              }}
            >
              {token.content}
            </span>
          ))
        : '\u00A0'}
    </div>
  ));

const reduceNewlines = (text: string) => text.replace(/\n{2,}/g, '\n');

interface SyntaxHighlighterProps extends StaticCodeProps {
  language: string;
  code: string;
  isAnimating?: boolean;
}

interface VirtualizedCodeChunkProps {
  canHighlight: boolean;
  chunkText: string;
  highlightPriority: number;
  isAnimating: boolean;
  lineClassName: string;
  language: string;
  syntaxTheme: SyntaxHighlightTheme;
}

interface StreamingCodeChunkProps extends VirtualizedCodeChunkProps {
  hasUnstableTail?: boolean;
  lineKeyPrefix: string;
}

const VirtualizedCodeChunk = React.memo(function VirtualizedCodeChunk({
  canHighlight,
  chunkText,
  highlightPriority,
  isAnimating,
  lineClassName,
  language,
  syntaxTheme,
}: VirtualizedCodeChunkProps) {
  const chunkHighlightKey = `${language}:${syntaxTheme}:${chunkText}`;
  const [tokenState, setTokenState] = useState<{
    key: string;
    lines: SyntaxHighlightLine[];
  } | null>(null);
  const tokenLines =
    tokenState?.key === chunkHighlightKey ? tokenState.lines : null;
  const cachedTokenLines = peekCachedCodeHighlight({
    code: chunkText,
    language: language as BundledLanguage,
    theme: syntaxTheme,
  });
  const visibleTokenLines = cachedTokenLines ?? tokenLines;
  const plainElements = useMemo(
    () => renderPlainCodeChunk(chunkText, lineClassName),
    [chunkText, lineClassName]
  );
  const highlightedElements = useMemo(
    () =>
      visibleTokenLines
        ? renderHighlightedLines(visibleTokenLines, { lineClassName })
        : null,
    [lineClassName, visibleTokenLines]
  );

  useEffect(() => {
    setTokenState(null);

    if (!canHighlight) return;

    const request = requestCodeHighlight({
      code: chunkText,
      language: language as BundledLanguage,
      priority: highlightPriority,
      throttle: isAnimating,
      theme: syntaxTheme,
    });

    request.promise
      .then((newTokenLines) => {
        setTokenState({ key: chunkHighlightKey, lines: newTokenLines });
      })
      .catch((error) => {
        if (isExpectedSyntaxHighlightError(error)) return;
        logError(error, 'Failed to highlight virtualized code chunk');
      });

    return () => {
      request.cancel();
    };
  }, [
    canHighlight,
    chunkText,
    chunkHighlightKey,
    highlightPriority,
    isAnimating,
    language,
    syntaxTheme,
  ]);

  return <>{highlightedElements ?? plainElements}</>;
});

const StreamingCodeChunk = React.memo(function StreamingCodeChunk({
  canHighlight,
  chunkText,
  hasUnstableTail = true,
  highlightPriority,
  isAnimating,
  lineClassName,
  lineKeyPrefix,
  language,
  syntaxTheme,
}: StreamingCodeChunkProps) {
  const chunkLines = useMemo(
    () => splitCodeLinesForHighlighting(chunkText),
    [chunkText]
  );
  const completedLines = useMemo(
    () =>
      hasUnstableTail && chunkLines.length > 0
        ? chunkLines.slice(0, -1)
        : chunkLines,
    [chunkLines, hasUnstableTail]
  );
  const tailLine = hasUnstableTail
    ? (chunkLines[chunkLines.length - 1] ?? '')
    : null;
  const completedCode = useMemo(
    () => completedLines.join('\n'),
    [completedLines]
  );
  const [highlightedPrefix, setHighlightedPrefix] = useState<{
    code: string;
    elements: React.ReactElement[];
    lineCount: number;
  } | null>(null);

  const canReusePrefix =
    highlightedPrefix !== null &&
    highlightedPrefix.lineCount <= completedLines.length &&
    completedLines.slice(0, highlightedPrefix.lineCount).join('\n') ===
      highlightedPrefix.code;
  const retainedPrefix = canReusePrefix ? highlightedPrefix : null;
  const retainedLineCount = retainedPrefix?.lineCount ?? 0;
  const pendingCompletedLines = completedLines.slice(retainedLineCount);

  useEffect(() => {
    if (!canReusePrefix) {
      setHighlightedPrefix(null);
    }
  }, [canReusePrefix]);

  useEffect(() => {
    if (
      !canHighlight ||
      completedCode.length === 0 ||
      (canReusePrefix && highlightedPrefix?.lineCount === completedLines.length)
    ) {
      return;
    }

    const request = requestCodeHighlight({
      code: completedCode,
      language: language as BundledLanguage,
      priority: highlightPriority,
      throttle: isAnimating,
      theme: syntaxTheme,
    });

    request.promise
      .then((newTokenLines) => {
        setHighlightedPrefix((currentPrefix) => {
          const currentLineCount = currentPrefix?.lineCount ?? 0;
          if (currentLineCount > newTokenLines.length) return currentPrefix;

          const canAppendToCurrent =
            currentPrefix !== null &&
            completedLines.slice(0, currentLineCount).join('\n') ===
              currentPrefix.code;
          const prefixElements = canAppendToCurrent
            ? currentPrefix.elements
            : [];
          const prefixLineCount = canAppendToCurrent ? currentLineCount : 0;
          const suffixTokenLines = newTokenLines.slice(prefixLineCount);
          const suffixElements = renderHighlightedLines(suffixTokenLines, {
            keyOffset: prefixLineCount,
            lineClassName,
          });

          return {
            code: completedLines.slice(0, newTokenLines.length).join('\n'),
            elements: [...prefixElements, ...suffixElements],
            lineCount: newTokenLines.length,
          };
        });
      })
      .catch((error) => {
        if (isExpectedSyntaxHighlightError(error)) return;
        logError(error, 'Failed to highlight streaming code prefix');
      });

    return () => {
      request.cancel();
    };
  }, [
    canHighlight,
    canReusePrefix,
    completedCode,
    completedLines,
    completedLines.length,
    highlightPriority,
    highlightedPrefix?.lineCount,
    isAnimating,
    language,
    lineClassName,
    syntaxTheme,
  ]);

  return (
    <>
      {retainedPrefix?.elements}
      {renderPlainLines(
        pendingCompletedLines,
        lineClassName,
        retainedLineCount
      )}
      {tailLine !== null ? (
        <div key={`${lineKeyPrefix}-tail`} className={lineClassName}>
          {tailLine || '\u00A0'}
        </div>
      ) : null}
    </>
  );
});

const SyntaxHighlighterInner = observer(function SyntaxHighlighter({
  code,
  isAnimating = false,
  language,
}: SyntaxHighlighterProps) {
  const isStaticExport = useContext(StaticExportContext);
  const theme = uiStore.theme;
  const lineWrap = isStaticExport || settingsStore.codeBlockLineWrap;
  const scale = settingsStore.typeScale;

  const [copied, setCopied] = useState(false);
  const [highlightState, setHighlightState] = useState<{
    elements: React.ReactElement[];
    key: string;
  } | null>(null);
  const virtualizedListRef = useRef<VListHandle>(null);
  const previousVirtualizedCodeLengthRef = useRef(code.length);
  const shouldFollowVirtualizedBottomRef = useRef(true);
  const [virtualizedScrollState, setVirtualizedScrollState] = useState({
    hasMeasured: false,
    hasMoreBelow: false,
    hasScrolled: false,
  });

  // Use viewport detection to delay highlighting until
  // the component is in viewport
  const { ref: viewportRef, inViewport } = useInViewport({
    enabled: !isStaticExport,
  });

  const getLineHeight = (scale: TypeScale) => {
    switch (scale) {
      case TypeScale.Small:
        return 15;
      case TypeScale.Normal:
        return 17.5;
      case TypeScale.ExtraLarge:
        return 22.5;
      case TypeScale.Large:
      default:
        return 20;
    }
  };

  const codeLines = useMemo(() => splitCodeLinesForHighlighting(code), [code]);
  const codeLineChunks = useMemo(
    () => chunkCodeLinesForHighlighting(codeLines),
    [codeLines]
  );

  const { minHeight, lineHeight, estimatedContentHeight } = useMemo(() => {
    const count = reduceNewlines(code).split('\n').length;
    const lineHeight = getLineHeight(scale);
    const paddingY = 20;

    return {
      minHeight: count * lineHeight + paddingY,
      lineHeight,
      estimatedContentHeight: codeLines.length * lineHeight + paddingY,
    };
  }, [code, codeLines.length, scale]);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  const shouldEagerHighlight = useMemo(
    () => shouldEagerHighlightCode(code, codeLines.length),
    [code, codeLines.length]
  );

  const shouldVirtualizeCode = useMemo(
    () =>
      !isStaticExport &&
      (isAnimating ||
        estimatedContentHeight > VIRTUALIZED_CODE_BLOCK_MAX_HEIGHT_PX ||
        (!shouldEagerHighlight && lineWrap)),
    [
      estimatedContentHeight,
      isAnimating,
      isStaticExport,
      lineWrap,
      shouldEagerHighlight,
    ]
  );

  const syntaxTheme = useMemo(
    () => (theme === 'dark' ? 'github-dark' : 'github-light'),
    [theme]
  );

  const eagerHighlightKey = useMemo(
    () => `${language}:${syntaxTheme}:${lineWrap ? 'wrap' : 'nowrap'}:${code}`,
    [code, language, lineWrap, syntaxTheme]
  );

  const cachedEagerTokenLines = useMemo(
    () =>
      shouldEagerHighlight && !shouldVirtualizeCode
        ? peekCachedCodeHighlight({
            code,
            language: language as BundledLanguage,
            theme: syntaxTheme,
          })
        : null,
    [code, language, shouldEagerHighlight, shouldVirtualizeCode, syntaxTheme]
  );

  const cachedEagerElements = useMemo(() => {
    if (!cachedEagerTokenLines) return null;

    const cachedElements = getCachedRenderedHighlight(eagerHighlightKey);
    if (cachedElements) return cachedElements;

    const elements = renderHighlightedLines(cachedEagerTokenLines, {
      lineWrap,
    });
    setCachedRenderedHighlight(eagerHighlightKey, elements);
    return elements;
  }, [cachedEagerTokenLines, eagerHighlightKey, lineWrap]);

  const updateVirtualizedScrollState = useCallback(
    (offset?: number, updateBottomFollow = false) => {
      const list = virtualizedListRef.current;
      if (!list) return;

      const scrollOffset = offset ?? list.scrollOffset;
      const hasMoreBelow =
        scrollOffset + list.viewportSize <
        list.scrollSize - VIRTUALIZED_CODE_BOTTOM_FOLLOW_THRESHOLD_PX;

      if (updateBottomFollow) {
        shouldFollowVirtualizedBottomRef.current = !hasMoreBelow;
      }

      const nextState = {
        hasMeasured: true,
        hasMoreBelow,
        hasScrolled: scrollOffset > VIRTUALIZED_CODE_SCROLL_THRESHOLD_PX,
      };

      setVirtualizedScrollState((currentState) =>
        currentState.hasMeasured === nextState.hasMeasured &&
        currentState.hasMoreBelow === nextState.hasMoreBelow &&
        currentState.hasScrolled === nextState.hasScrolled
          ? currentState
          : nextState
      );
    },
    []
  );

  const handleVirtualizedScroll = useCallback(
    (offset: number) => {
      updateVirtualizedScrollState(offset, true);
    },
    [updateVirtualizedScrollState]
  );

  useEffect(() => {
    if (!shouldVirtualizeCode || !inViewport) {
      setVirtualizedScrollState({
        hasMeasured: false,
        hasMoreBelow: false,
        hasScrolled: false,
      });
      return;
    }

    const animationFrame = requestAnimationFrame(() => {
      updateVirtualizedScrollState();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [inViewport, shouldVirtualizeCode, updateVirtualizedScrollState]);

  useEffect(() => {
    const previousCodeLength = previousVirtualizedCodeLengthRef.current;
    previousVirtualizedCodeLengthRef.current = code.length;

    if (
      !shouldVirtualizeCode ||
      !inViewport ||
      code.length <= previousCodeLength ||
      !shouldFollowVirtualizedBottomRef.current
    ) {
      return;
    }

    const animationFrame = requestAnimationFrame(() => {
      const list = virtualizedListRef.current;
      if (!list) return;

      list.scrollTo(Math.max(0, list.scrollSize - list.viewportSize));
      updateVirtualizedScrollState();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [
    code.length,
    inViewport,
    shouldVirtualizeCode,
    updateVirtualizedScrollState,
  ]);

  useEffect(() => {
    // Only run highlighting when in viewport for performance
    if (!inViewport) return;

    if (!shouldEagerHighlight || shouldVirtualizeCode) {
      setHighlightState(null);
      // Long code is highlighted incrementally in the virtualized chunk path
      return;
    }

    if (cachedEagerTokenLines) return;

    setHighlightState(null);
    const request = requestCodeHighlight({
      code,
      language: language as BundledLanguage,
      throttle: isAnimating,
      theme: syntaxTheme,
    });

    request.promise
      .then((newTokenLines) => {
        const elements = renderHighlightedLines(newTokenLines, { lineWrap });
        setCachedRenderedHighlight(eagerHighlightKey, elements);
        setHighlightState({
          elements,
          key: eagerHighlightKey,
        });
      })
      .catch((error) => {
        if (isExpectedSyntaxHighlightError(error)) return;
        logError(error, 'Failed to highlight syntax');
        // Keep showing raw code on error
      });

    return () => {
      request.cancel();
    };
  }, [
    shouldEagerHighlight,
    shouldVirtualizeCode,
    eagerHighlightKey,
    inViewport,
    isAnimating,
    syntaxTheme,
    code,
    cachedEagerTokenLines,
    language,
    lineWrap,
  ]);

  const containerClassName = useMemo(
    () =>
      classList(
        'group/code-block',
        'font-mono',
        'relative',
        'bg-surface',
        'border border-border',
        'rounded-lg smoothing-default',
        'flex flex-col',
        'overflow-hidden'
      ),
    []
  );

  const codeClassName = useMemo(
    () =>
      classList(
        'code-block',
        `language-${language}`,
        'select-text',
        'text-gray-12',
        {
          'whitespace-pre-wrap break-words': lineWrap,
        }
      ),
    [language, lineWrap]
  );

  const infoClassName = useMemo(
    () =>
      classList(
        'border-t border-border',
        'font-sans text-foreground-subtle text-xs',
        'px-2 py-1',
        'flex items-center gap-0.5',
        'mt-1',
        'cursor-default'
      ),
    []
  );

  const copyIcon = useMemo(() => (copied ? Icons.check : Icons.copy), [copied]);

  const virtualizedLineClassName = useMemo(
    () =>
      classList('min-h-[1.5em]', {
        'whitespace-pre-wrap break-words': lineWrap,
        'whitespace-pre': !lineWrap,
      }),
    [lineWrap]
  );

  const content = useMemo(() => {
    if (cachedEagerElements) return cachedEagerElements;
    const highlightedElements =
      highlightState?.key === eagerHighlightKey ? highlightState.elements : [];

    // Show highlighted version if available, otherwise show raw code
    if (
      !shouldEagerHighlight ||
      shouldVirtualizeCode ||
      highlightedElements.length === 0
    ) {
      return code;
    }
    return highlightedElements;
  }, [
    cachedEagerElements,
    eagerHighlightKey,
    shouldEagerHighlight,
    shouldVirtualizeCode,
    code,
    highlightState,
  ]);

  const virtualizedCodeBlockStyle = useMemo(
    () => ({
      height: isAnimating
        ? `min(50vh, ${VIRTUALIZED_CODE_BLOCK_MAX_HEIGHT_PX}px, ${Math.ceil(
            estimatedContentHeight
          )}px)`
        : `min(50vh, ${VIRTUALIZED_CODE_BLOCK_MAX_HEIGHT_PX}px)`,
    }),
    [estimatedContentHeight, isAnimating]
  );

  const headerClassName = useMemo(
    () =>
      classList(
        'flex items-center justify-between pl-3 pr-1 pt-1 pb-1',
        'border-b transition-colors',
        {
          'border-border':
            shouldVirtualizeCode && virtualizedScrollState.hasScrolled,
          'border-transparent':
            !shouldVirtualizeCode || !virtualizedScrollState.hasScrolled,
        }
      ),
    [shouldVirtualizeCode, virtualizedScrollState.hasScrolled]
  );

  const isVirtualizedBottomFadeVisible =
    shouldVirtualizeCode &&
    inViewport &&
    (virtualizedScrollState.hasMeasured
      ? virtualizedScrollState.hasMoreBelow
      : estimatedContentHeight > VIRTUALIZED_CODE_BLOCK_MAX_HEIGHT_PX);

  const virtualizedBottomFadeClassName = useMemo(
    () =>
      classList(
        'pointer-events-none absolute inset-x-0 bottom-0 h-12',
        'bg-gradient-to-b from-transparent via-surface/80 to-surface',
        {
          'opacity-100': isVirtualizedBottomFadeVisible,
          'opacity-0': !isVirtualizedBottomFadeVisible,
        }
      ),
    [isVirtualizedBottomFadeVisible]
  );

  const preClassName = useMemo(
    () =>
      classList('smoothdown-code-block-pre text-sm p-0 rounded-none', {
        'leading-none': shouldVirtualizeCode,
      }),
    [shouldVirtualizeCode]
  );

  return (
    <div
      ref={viewportRef}
      data-render-pending={
        isStaticExport && shouldEagerHighlight && content === code
          ? 'highlight'
          : undefined
      }
      style={shouldVirtualizeCode ? undefined : { minHeight: `${minHeight}px` }}
      className={containerClassName}
    >
      <div data-find-field-exclude className={headerClassName}>
        <span className="text-xs text-foreground font-sans">{language}</span>
        <IconButton
          icon={copyIcon}
          onClick={copy}
          className="transition-opacity opacity-0 touch:opacity-100 group-hover/code-block:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]/code-block:opacity-100"
          size="xs"
        />
      </div>

      <pre className={preClassName}>
        {shouldVirtualizeCode ? (
          <div className="relative overflow-hidden">
            <code className={codeClassName}>
              <VList
                ref={virtualizedListRef}
                count={codeLineChunks.length}
                itemSize={
                  isAnimating || lineWrap
                    ? undefined
                    : lineHeight * VIRTUALIZED_CODE_CHUNK_LINE_COUNT
                }
                overscan={VIRTUALIZED_CODE_BLOCK_OVERSCAN}
                onScroll={handleVirtualizedScroll}
                className="px-3 pb-2 scrollbar-hover"
                style={virtualizedCodeBlockStyle}
              >
                {(index) => {
                  const chunkText = codeLineChunks[index] ?? '';

                  return isAnimating ? (
                    <StreamingCodeChunk
                      canHighlight={inViewport}
                      hasUnstableTail={index === codeLineChunks.length - 1}
                      chunkText={chunkText}
                      highlightPriority={index}
                      isAnimating={isAnimating}
                      lineClassName={virtualizedLineClassName}
                      lineKeyPrefix={`virtualized-streaming-${index}`}
                      language={language}
                      syntaxTheme={syntaxTheme}
                    />
                  ) : (
                    <VirtualizedCodeChunk
                      canHighlight={inViewport}
                      chunkText={chunkText}
                      highlightPriority={index}
                      isAnimating={isAnimating}
                      lineClassName={virtualizedLineClassName}
                      language={language}
                      syntaxTheme={syntaxTheme}
                    />
                  );
                }}
              </VList>
            </code>
            {shouldVirtualizeCode && (
              <div
                aria-hidden="true"
                className={virtualizedBottomFadeClassName}
              />
            )}
          </div>
        ) : (
          <div className="px-3 pb-2">
            <code className={codeClassName}>{content}</code>
          </div>
        )}

        {!shouldEagerHighlight && !shouldVirtualizeCode && !isStaticExport && (
          <div className={infoClassName}>
            <Icons.info size={12} /> Syntax highlighting is disabled for large
            code blocks
          </div>
        )}
      </pre>
    </div>
  );
});

export const SyntaxHighlighter = React.memo(
  SyntaxHighlighterInner,
  (prevProps, nextProps) =>
    prevProps.code === nextProps.code &&
    prevProps.className === nextProps.className &&
    prevProps.isAnimating === nextProps.isAnimating &&
    prevProps.language === nextProps.language
);
